import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { searchApi } from './search.api';

function useSearchIdentity() {
  const { user, tenant, permissions } = useAuth();
  return { tenantId: tenant?.id ?? user?.tenantId ?? 'none', userId: user?.id ?? 'none', permissions: [...permissions].sort().join('|'), enabled: Boolean(user?.id) && hasPermission(permissions, PERMISSIONS.SEARCH_READ) };
}
export const searchKeys = {
  root: (identity: { tenantId: string; userId: string; permissions: string }) => ['search', identity.tenantId, identity.userId, identity.permissions] as const,
};

export function useDebouncedValue(value: string, delayMs = 200): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
}

export function useGlobalSearch(query: string, enabled = true, limit = 20) {
  const identity = useSearchIdentity();
  const trimmed = query.trim();
  return useQuery({
    queryKey: [...searchKeys.root(identity), 'global', trimmed, limit] as const,
    enabled: enabled && identity.enabled && trimmed.length >= 2,
    queryFn: async () => {
      const result = await searchApi.search(trimmed, limit);
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to search');
      }
      return result.data;
    },
  });
}

export function useSearchCommandCenter(enabled = true) {
  const identity = useSearchIdentity();
  return useQuery({
    queryKey: [...searchKeys.root(identity), 'command-center'] as const,
    enabled: enabled && identity.enabled,
    queryFn: async () => {
      const result = await searchApi.getCommandCenter();
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load search command center');
      }
      return result.data;
    },
  });
}
